import { useEffect, useRef, useState } from "react";
import { useMatchMedia } from "@/lib/useMatchMedia";
import {
  MOBILE_HERO_MAX_WIDTH,
  SPLINE_RENDER_HEIGHT,
  SPLINE_RENDER_HEIGHT_MOBILE,
  SPLINE_RENDER_WIDTH,
  SPLINE_RENDER_WIDTH_MOBILE,
} from "@/components/sections/hero/heroShared";
import "./SplineScaledIframe.css";

function fitScale(mode, containerW, containerH, renderW, renderH) {
  if (!containerW || !containerH || !renderW || !renderH) return 1;
  if (mode === "width") return containerW / renderW;
  if (mode === "contain") {
    return Math.min(containerW / renderW, containerH / renderH);
  }
  return Math.max(containerW / renderW, containerH / renderH);
}

/**
 * Caps Spline iframe WebGL resolution, then scales up to fill the hero (object-fit: cover).
 * Same visuals at lower GPU cost — similar to viewing full page in a narrow Cursor panel.
 */
export default function SplineScaledIframe({
  src,
  title,
  className = "",
  iframeClassName = "",
  stageClassName = "",
  allow = "fullscreen",
  loading = "lazy",
  renderWidth,
  renderHeight,
  scaleMode = "cover",
  scaleInset = 1,
  stageOffsetY = 0,
  onLoad,
}) {
  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);
  const isMobile = useMatchMedia(`(max-width: ${MOBILE_HERO_MAX_WIDTH}px)`);
  const baseW =
    renderWidth ?? (isMobile ? SPLINE_RENDER_WIDTH_MOBILE : SPLINE_RENDER_WIDTH);
  const baseH =
    renderHeight ?? (isMobile ? SPLINE_RENDER_HEIGHT_MOBILE : SPLINE_RENDER_HEIGHT);
  const inset = Number.isFinite(scaleInset) ? scaleInset : 1;

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;

    const update = () => {
      const { width, height } = node.getBoundingClientRect();
      setScale(fitScale(scaleMode, width, height, baseW, baseH) * inset);
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(node);
    window.addEventListener("orientationchange", update);

    return () => {
      observer.disconnect();
      window.removeEventListener("orientationchange", update);
    };
  }, [baseW, baseH, scaleMode, inset]);

  return (
    <div ref={containerRef} className={`ds-spline-scaled-frame ${className}`.trim()}>
      <div
        className={`ds-spline-scaled-frame__stage ${stageClassName}`.trim()}
        style={{
          width: baseW,
          height: baseH,
          "--spline-cover-scale": scale,
          "--spline-stage-offset-y": `${stageOffsetY}%`,
        }}
      >
        <iframe
          src={src}
          title={title}
          width={baseW}
          height={baseH}
          className={iframeClassName}
          loading={loading}
          allow={allow}
          frameBorder="0"
          onLoad={onLoad}
        />
      </div>
    </div>
  );
}
